import type { Point, Rect } from './types';

/**
 * 3x3 matrix in column-major order, matching what `uniformMatrix3fv` expects:
 *
 *     m[0] m[3] m[6]
 *     m[1] m[4] m[7]
 *     m[2] m[5] m[8]
 */
export type Mat3 = Float32Array;

export function mat3Identity(): Mat3 {
  return new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
}

export function mat3Translation(tx: number, ty: number): Mat3 {
  return new Float32Array([1, 0, 0, 0, 1, 0, tx, ty, 1]);
}

export function mat3Scaling(sx: number, sy: number): Mat3 {
  return new Float32Array([sx, 0, 0, 0, sy, 0, 0, 0, 1]);
}

export function mat3Rotation(radians: number): Mat3 {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return new Float32Array([c, s, 0, -s, c, 0, 0, 0, 1]);
}

/** Shear where `kx` slants x by y and `ky` slants y by x. */
export function mat3Skew(kx: number, ky: number): Mat3 {
  return new Float32Array([1, ky, 0, kx, 1, 0, 0, 0, 1]);
}

export function mat3Multiply(a: Mat3, b: Mat3): Mat3 {
  const out = new Float32Array(9);
  for (let col = 0; col < 3; col += 1) {
    for (let row = 0; row < 3; row += 1) {
      out[col * 3 + row] =
        a[row] * b[col * 3] + a[3 + row] * b[col * 3 + 1] + a[6 + row] * b[col * 3 + 2];
    }
  }
  return out;
}

export function mat3MultiplyAll(...matrices: Mat3[]): Mat3 {
  return matrices.reduce((acc, m) => mat3Multiply(acc, m), mat3Identity());
}

export function mat3Invert(m: Mat3): Mat3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const det = a * (e * i - f * h) - d * (b * i - c * h) + g * (b * f - c * e);
  if (Math.abs(det) < 1e-12) return mat3Identity();
  const inv = 1 / det;
  return new Float32Array([
    (e * i - f * h) * inv,
    (c * h - b * i) * inv,
    (b * f - c * e) * inv,
    (f * g - d * i) * inv,
    (a * i - c * g) * inv,
    (c * d - a * f) * inv,
    (d * h - e * g) * inv,
    (b * g - a * h) * inv,
    (a * e - b * d) * inv,
  ]);
}

/** Projective transform; `w` is 1 for affine matrices, so this is always correct. */
export function mat3TransformPoint(m: Mat3, x: number, y: number): Point {
  const w = m[2] * x + m[5] * y + m[8];
  const scale = Math.abs(w) < 1e-12 ? 0 : 1 / w;
  return {
    x: (m[0] * x + m[3] * y + m[6]) * scale,
    y: (m[1] * x + m[4] * y + m[7]) * scale,
  };
}

/**
 * Whole-pixel translation encoded in a document-space matrix, or null when the
 * linear part is not identity (scale / rotate / skew / perspective).
 */
export function mat3PureTranslation(m: Mat3, epsilon = 1e-4): Point | null {
  if (
    Math.abs(m[0] - 1) > epsilon ||
    Math.abs(m[1]) > epsilon ||
    Math.abs(m[2]) > epsilon ||
    Math.abs(m[3]) > epsilon ||
    Math.abs(m[4] - 1) > epsilon ||
    Math.abs(m[5]) > epsilon ||
    Math.abs(m[8] - 1) > epsilon
  ) {
    return null;
  }
  return { x: Math.round(m[6]), y: Math.round(m[7]) };
}

/**
 * Homography that maps the unit square to an arbitrary convex quad, with corners
 * given for (0,0), (1,0), (1,1) and (0,1). Heckbert's construction: an affine
 * matrix when the quad is a parallelogram, projective otherwise.
 */
export function mat3FromUnitQuad(corners: readonly [Point, Point, Point, Point]): Mat3 {
  const [p0, p1, p2, p3] = corners;
  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;

  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) {
    return new Float32Array([
      p1.x - p0.x,
      p1.y - p0.y,
      0,
      p3.x - p0.x,
      p3.y - p0.y,
      0,
      p0.x,
      p0.y,
      1,
    ]);
  }

  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const den = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(den) < 1e-12) return mat3Identity();

  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;
  return new Float32Array([
    p1.x - p0.x + g * p1.x,
    p1.y - p0.y + g * p1.y,
    g,
    p3.x - p0.x + h * p3.x,
    p3.y - p0.y + h * p3.y,
    h,
    p0.x,
    p0.y,
    1,
  ]);
}

/** Homography that maps a rectangle's corners, clockwise from top left, to a quad. */
export function mat3RectToQuad(rect: Rect, corners: readonly [Point, Point, Point, Point]): Mat3 {
  const width = rect.width || 1;
  const height = rect.height || 1;
  return mat3Multiply(
    mat3FromUnitQuad(corners),
    mat3MultiplyAll(mat3Scaling(1 / width, 1 / height), mat3Translation(-rect.x, -rect.y)),
  );
}

/**
 * Builds the affine transform of a free-transform session: the layer is scaled,
 * rotated and skewed around `origin` (document space), then translated.
 */
export function composeTransform(params: {
  origin: Point;
  translateX: number;
  translateY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  skewX: number;
  skewY: number;
}): Mat3 {
  const { origin } = params;
  return mat3MultiplyAll(
    mat3Translation(origin.x + params.translateX, origin.y + params.translateY),
    mat3Rotation(params.rotation),
    mat3Skew(params.skewX, params.skewY),
    mat3Scaling(params.scaleX, params.scaleY),
    mat3Translation(-origin.x, -origin.y),
  );
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function rectFromPoints(a: Point, b: Point): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

export function rectIsEmpty(r: Rect): boolean {
  return r.width <= 0 || r.height <= 0;
}

export function rectUnion(a: Rect, b: Rect): Rect {
  if (rectIsEmpty(a)) return { ...b };
  if (rectIsEmpty(b)) return { ...a };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

export function rectIntersect(a: Rect, b: Rect): Rect {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}

/** Snaps to whole pixels outwards, then clips to `0,0,width,height`. */
export function rectAlignToPixels(r: Rect, width: number, height: number): Rect {
  const x = Math.max(0, Math.floor(r.x));
  const y = Math.max(0, Math.floor(r.y));
  const right = Math.min(width, Math.ceil(r.x + r.width));
  const bottom = Math.min(height, Math.ceil(r.y + r.height));
  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}

export function rectExpand(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, width: r.width + by * 2, height: r.height + by * 2 };
}

export function rectContains(r: Rect, p: Point): boolean {
  return p.x >= r.x && p.y >= r.y && p.x <= r.x + r.width && p.y <= r.y + r.height;
}

/**
 * Snap `to` onto the nearest 0° / 45° / 90° ray from `from`, keeping the same
 * distance — Photoshop's Shift+drag constraint for paint strokes.
 */
export function constrainToOctant(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 1e-4) return { x: to.x, y: to.y };
  const step = Math.PI / 4;
  const snapped = Math.round(Math.atan2(dy, dx) / step) * step;
  return {
    x: from.x + Math.cos(snapped) * distance,
    y: from.y + Math.sin(snapped) * distance,
  };
}
